import {SearchResultViewType} from '../core/definitions/search-result-view-type.enum';
import {SearchContainer} from '../core/definitions/search-container';
import {ContentListSourceContainer} from '../core/definitions/object-content-tab/content-list-source-container';

export interface ContentListChoice {
  viewName?: string;
  templateId?: string;
}

export function contentListChoice(
  viewName: string | undefined,
  templateId: string | undefined,
): ContentListChoice {
  const choice: ContentListChoice = {};
  if (viewName === SearchResultViewType.LIST) {
    choice.viewName = SearchResultViewType.LIST;
  }
  if (templateId) {
    choice.templateId = templateId;
  }
  return choice;
}

export function parseContentListChoice(raw: string | null | undefined): ContentListChoice {
  if (!raw) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw) as ContentListChoice;
    return contentListChoice(parsed?.viewName, parsed?.templateId);
  } catch {
    return {};
  }
}

export function restoreContentListChoice(
  source: ContentListSourceContainer | undefined,
  viewName: string | undefined,
  templateId: string | undefined,
): void {
  if (!source) {
    return;
  }
  if (viewName === SearchResultViewType.LIST) {
    source.resultViewName = SearchResultViewType.LIST;
    if (source.searchContainer) {
      applyContentListView(source.searchContainer, SearchResultViewType.LIST);
    }
  }
  if (!templateId || !source.searchContainer) {
    return;
  }
  source.searchContainer.listFieldTemplateId = templateId;
  source.searchContainer.refreshTemplateOverviewFields = true;
  for (const operation of [source.searchContainer.operationContainer, source.operationContainer]) {
    if (!operation) {
      continue;
    }
    operation.listFieldTemplateId = templateId;
  }
}

export function isContentListTable(viewName: string | undefined): boolean {
  return viewName === SearchResultViewType.LIST;
}

export function applyContentListView(searchContainer: SearchContainer, viewName: string): void {
  searchContainer.searchResultViewName = viewName;
  searchContainer.combineHorizontal = viewName !== SearchResultViewType.LIST;
  if (viewName !== SearchResultViewType.LIST || typeof searchContainer.rows === 'number') {
    return;
  }
  const rows = searchContainer.rows || {};
  searchContainer.rows = rows;
  if (rows[SearchResultViewType.LIST] == null) {
    const cardRows = rows[SearchResultViewType.CONTENT_LIST];
    rows[SearchResultViewType.LIST] = typeof cardRows === 'number' ? cardRows : 5;
  }
}

export function carryContentListView(
  from: ContentListSourceContainer | undefined,
  to: ContentListSourceContainer | undefined,
): void {
  if (!from || !to || from === to || !from.resultViewName) {
    return;
  }
  to.resultViewName = from.resultViewName;
  if (to.searchContainer) {
    applyContentListView(to.searchContainer, from.resultViewName);
  }
}

export function selectProcedureObjectList(
  current: ContentListSourceContainer | undefined,
  viewName: string,
  stepSource: ContentListSourceContainer | undefined,
  allObjectsSource: ContentListSourceContainer | undefined,
): ContentListSourceContainer | undefined {
  const next = viewName === 'steps' ? stepSource : allObjectsSource;
  carryContentListView(current, next);
  carryContentListFieldTemplate(current, next);
  return next;
}

export function carryContentListFieldTemplate(
  from: ContentListSourceContainer | undefined,
  to: ContentListSourceContainer | undefined,
): void {
  if (!from?.searchContainer || !to?.searchContainer || from === to) {
    return;
  }
  const templateId = from.searchContainer.listFieldTemplateId;
  to.searchContainer.listFieldTemplateId = templateId;
  to.searchContainer.refreshTemplateOverviewFields = Boolean(templateId);
  const templateName = templateId
    ? from.searchContainer.operationContainer?.listFieldTemplateName
      || from.operationContainer?.listFieldTemplateName
    : undefined;
  for (const operation of [to.searchContainer.operationContainer, to.operationContainer]) {
    if (!operation) {
      continue;
    }
    operation.listFieldTemplateId = templateId;
    operation.listFieldTemplateName = templateName;
  }
  carryOverviewFields(from, to, templateId);
}

function carryOverviewFields(
  from: ContentListSourceContainer,
  to: ContentListSourceContainer,
  templateId: string | undefined,
): void {
  const toView = to.searchContainer.currentPathView?.search_view;
  const fromFields = from.searchContainer.currentPathView?.search_view?.overview_fields;
  if (!toView) {
    return;
  }
  if (templateId && fromFields) {
    toView.overview_fields = fromFields.slice();
    return;
  }
  if (toView.$$origOverviewFields) {
    toView.overview_fields = toView.$$origOverviewFields;
    toView.$$origOverviewFields = undefined;
  }
}
